<template>
    <div class="ui-page">
        <ui-page-header :title="data ? `Métricas · ${data.month_label}` : 'Métricas'" subtitle="Reunião mensal (última sexta). Tudo sai das mesmas telas da prospecção: casos, andamentos, agendas e nomeações.">
            <v-btn outlined @click="shift(-1)"><v-icon small>mdi-chevron-left</v-icon> {{ data ? data.previous_label.split("/")[0] : "" }}</v-btn>
            <v-select v-model="month" :items="monthItems" dense outlined hide-details :menu-props="{ offsetY: true }" style="max-width: 190px;" aria-label="Mês" @change="load"></v-select>
            <v-btn color="primary" depressed :loading="pdfLoading" @click="presentation"><v-icon left small>mdi-file-pdf-box</v-icon>Gerar apresentação (PDF)</v-btn>
        </ui-page-header>

        <template v-if="data">
            <section class="pmx-stats mb-5" aria-label="Indicadores do mês">
                <div v-for="kpi in kpis" :key="kpi.label" class="pmx-stat">
                    <span class="pmx-stat__value">{{ kpi.value }}</span>
                    <span class="pmx-stat__label">{{ kpi.label }}</span>
                    <span class="pmx-stat__hint">{{ kpi.hint }}</span>
                </div>
            </section>

            <div class="pmx-grid">
                <ui-section :title="`RJs e cautelares novas por mês · ${data.month.slice(0, 4)}`" description="Data de distribuição (ou de captação) dos casos na plataforma.">
                    <div class="pmx-series">
                        <div v-for="item in data.series" :key="item.month" class="pmx-series__item" :class="{ 'pmx-series__item--current': item.month === data.month }">
                            <div class="pmx-series__bar"><span :style="{ height: `${Math.max(3, (item.count / seriesMax) * 100)}%` }"></span></div>
                            <div class="pmx-series__count">{{ item.count }}</div>
                            <div class="pmx-series__label">{{ item.label }}</div>
                        </div>
                    </div>
                </ui-section>

                <ui-section title="Prospectado ativamente × não" description="Casos novos do mês com movimento BeJ (onepage, e-mail ou agenda).">
                    <div class="pmx-split">
                        <div class="pmx-split__ring" :style="{ background: `conic-gradient(var(--pmx-accent) 0 ${prospectedRate}%, var(--pmx-soft) ${prospectedRate}% 100%)` }">
                            <span>{{ prospectedRate }}%</span>
                        </div>
                        <div class="ui-small">
                            <div><strong>{{ data.prospected.active }}</strong> com movimento BeJ</div>
                            <div><strong>{{ data.prospected.inactive }}</strong> sem movimento · {{ data.prospected.low_debt }} por passivo baixo · {{ data.prospected.decided }} por decisão</div>
                        </div>
                    </div>
                    <div class="ui-divider"></div>
                    <table class="pmx-table">
                        <thead><tr><th>Agendas por pessoa</th><th class="r">{{ data.month_label.split("/")[0].slice(0, 3) }}</th><th class="r">{{ data.month.slice(0, 4) }}</th><th class="r">→ nomeação</th></tr></thead>
                        <tbody>
                            <tr v-for="item in data.meetings_by_person" :key="item.id"><td>{{ item.name }}</td><td class="r">{{ item.month }}</td><td class="r">{{ item.year }}</td><td class="r">{{ item.won }}</td></tr>
                            <tr v-if="!data.meetings_by_person.length"><td colspan="4" class="ui-muted">Nenhuma agenda realizada no ano.</td></tr>
                        </tbody>
                    </table>
                </ui-section>
            </div>

            <div class="pmx-grid mt-4">
                <ui-section :title="`Onde estão os maiores passivos · por UF · ${data.month.slice(0, 4)}`" description="Passivo somado de RJ e TCA (declarado ou valor da causa).">
                    <ui-empty v-if="!data.by_uf_amount.length" text="Sem casos no ano." />
                    <div v-for="item in data.by_uf_amount" :key="item.uf" class="pmx-bar">
                        <span class="pmx-bar__uf">{{ item.uf }}</span>
                        <span class="pmx-bar__track"><span :style="{ width: `${(item.amount / amountMax) * 100}%` }"></span></span>
                        <span class="pmx-bar__value">{{ formatMoneyShort(item.amount) }}</span>
                        <span class="pmx-bar__count">{{ item.count }} casos</span>
                    </div>
                </ui-section>

                <ui-section :title="`Juízes que recebem os maiores passivos · ${data.month.slice(0, 4)}`">
                    <ui-empty v-if="!data.top_judges.length" text="Nenhum caso com juiz identificado." />
                    <table v-else class="pmx-table">
                        <thead><tr><th>Juiz(a)</th><th>Vara</th><th class="r">Casos</th><th class="r">Passivo</th><th>BeJ</th></tr></thead>
                        <tbody>
                            <tr v-for="item in data.top_judges" :key="item.judge_id" @click="$router.push(`/prospeccao/juizes/${item.judge_id}`)" class="pmx-click">
                                <td>{{ item.judge_name || "—" }}</td>
                                <td class="ui-small">{{ item.uf }} · {{ item.court_name || "—" }}</td>
                                <td class="r">{{ item.count }}</td>
                                <td class="r ui-nowrap">{{ formatMoneyShort(item.amount) }}</td>
                                <td><ui-badge :status="levelBadge(item.level)" :text="item.level_label" /></td>
                            </tr>
                        </tbody>
                    </table>
                </ui-section>
            </div>

            <div class="pmx-grid mt-4">
                <ui-section title="Top 5 do mês · maiores casos novos">
                    <ui-empty v-if="!data.top_cases.length" text="Nenhum caso novo no mês." />
                    <table v-else class="pmx-table">
                        <thead><tr><th>#</th><th>Empresa</th><th>UF · comarca</th><th class="r">Passivo</th><th>Adv. do devedor</th><th>BeJ</th></tr></thead>
                        <tbody>
                            <tr v-for="(item, index) in data.top_cases" :key="item.id" class="pmx-click" @click="$router.push(`/prospeccao/casos/${item.id}`)">
                                <td>{{ index + 1 }}</td>
                                <td><strong>{{ item.company_name }}</strong> <span class="ui-muted">· {{ item.type === "tca" ? "TCA" : "RJ" }}</span></td>
                                <td>{{ item.uf || "—" }} · {{ item.city || item.court_name || "—" }}</td>
                                <td class="r ui-nowrap">{{ formatMoneyShort(item.amount) }}</td>
                                <td class="ui-small">{{ item.debtor_lawyer || "a preencher" }}</td>
                                <td>{{ item.aj_house ? "AJ BeJ" : (item.pp_house ? "PP BeJ" : item.pipeline_status_label) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </ui-section>

                <ui-section :title="`Novos casos por UF · ${data.month_label.split('/')[0]} × ${data.previous_label.split('/')[0]}`">
                    <ui-empty v-if="!data.by_uf.length" text="Nenhum caso novo." />
                    <table v-else class="pmx-table">
                        <thead><tr><th>UF</th><th class="r">{{ data.month_label.split("/")[0].slice(0, 3) }}</th><th class="r">{{ data.previous_label.split("/")[0].slice(0, 3) }}</th><th class="r">Pendentes AJ</th><th>Comarcas mais ativas</th></tr></thead>
                        <tbody>
                            <tr v-for="item in data.by_uf" :key="item.uf">
                                <td><strong>{{ item.uf }}</strong></td>
                                <td class="r">{{ item.month }}</td>
                                <td class="r ui-muted">{{ item.previous }}</td>
                                <td class="r">{{ item.pending_aj }}</td>
                                <td class="ui-small">{{ item.cities.join(", ") || "—" }}</td>
                            </tr>
                        </tbody>
                    </table>
                </ui-section>
            </div>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import { formatMoneyShort, notifyError, openPdf, todayKey } from "@/components/prospect/prospect.util";

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

function shiftMonth(key, delta) {
    const [year, month] = key.split("-").map(Number);
    const index = year * 12 + (month - 1) + delta;
    return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

export default {
    name: "ProspectMetrics",

    data: () => ({
        data: null,
        month: shiftMonth(todayKey().slice(0, 7), -1),
        pdfLoading: false,
    }),

    computed: {
        monthItems() {
            const current = todayKey().slice(0, 7);
            return Array.from({ length: 18 }, (_, index) => {
                const key = shiftMonth(current, -index);
                const [year, month] = key.split("-").map(Number);
                return { value: key, text: `${MONTHS[month - 1]}/${year}` };
            });
        },
        kpis() {
            const kpis = this.data.kpis;
            const people = kpis.meetings_by_person.filter((item) => item.month).map((item) => `${item.name} ${item.month}`).join(" · ");
            return [
                { value: kpis.new_cases, label: "RJs + TCAs novas no mês", hint: `${this.data.previous_label.split("/")[0].slice(0, 3)}: ${kpis.previous_cases}` },
                { value: kpis.onepages, label: "onepages elaborados", hint: "casos com onepage no mês" },
                { value: kpis.meetings, label: "agendas realizadas", hint: people || "nenhuma no mês" },
                { value: kpis.active, label: "casos em prospecção ativa", hint: "acompanhar, onepage, agenda, aguardando" },
                { value: `${kpis.appointments.pp} · ${kpis.appointments.aj}`, label: "nomeações BeJ no mês", hint: "PP · AJ" },
                { value: formatMoneyShort(kpis.new_amount), label: "passivo novo captado", hint: "declarado ou valor da causa" },
            ];
        },
        seriesMax() {
            return Math.max(1, ...this.data.series.map((item) => item.count));
        },
        amountMax() {
            return Math.max(1, ...this.data.by_uf_amount.map((item) => item.amount));
        },
        prospectedRate() {
            const total = this.data.prospected.total;
            return total ? Math.round((this.data.prospected.active / total) * 100) : 0;
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatMoneyShort,

        levelBadge(level) {
            return { nomeou_bej: "approved", agenda_feita: "active", contato: "pending" }[level] || "inactive";
        },

        shift(delta) {
            this.month = shiftMonth(this.month, delta);
            this.load();
        },

        async load() {
            try {
                const result = await axios.get("/api/admin/prospect/metrics", { params: { month: this.month } });
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        async presentation() {
            this.pdfLoading = true;
            try {
                await openPdf(axios, "/api/admin/prospect/reports/metrics.pdf", { month: this.month });
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.pdfLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.ui-page {
    --pmx-accent: #{$ui-accent};
    --pmx-soft: #{$ui-border-soft};
}

.pmx-stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

.pmx-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 14px 16px;
    border-right: 1px solid $ui-border;

    &:last-child {
        border-right: 0;
    }
}

.pmx-stat__value {
    font-size: 24px;
    font-weight: 600;
    color: $ui-fg;
}

.pmx-stat__label {
    font-size: 13px;
    color: $ui-text;
}

.pmx-stat__hint {
    font-size: 12px;
    color: $ui-muted;
}

.pmx-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

// Cartoes lado a lado: sem o espacamento vertical de ".ui-card + .ui-card".
.pmx-grid > .ui-card + .ui-card {
    margin-top: 0;
}

.pmx-series {
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    gap: 6px;
    align-items: end;
}

.pmx-series__bar {
    display: flex;
    align-items: flex-end;
    height: 120px;

    span {
        display: block;
        width: 100%;
        border-radius: 5px 5px 0 0;
        background: nth(map-get($ui-tones, neutral), 3);
    }
}

.pmx-series__item--current .pmx-series__bar span {
    background: $ui-accent;
}

.pmx-series__count {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
}

.pmx-series__label {
    font-size: 11px;
    text-align: center;
    color: $ui-muted;
}

.pmx-split {
    display: flex;
    align-items: center;
    gap: 16px;
}

.pmx-split__ring {
    position: relative;
    flex: 0 0 auto;
    width: 84px;
    height: 84px;
    border-radius: 50%;

    span {
        position: absolute;
        inset: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: $ui-bg;
        font-weight: 600;
    }
}

.pmx-bar {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) 80px 70px;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 13px;
}

.pmx-bar__uf {
    font-weight: 600;
}

.pmx-bar__track {
    height: 10px;
    border-radius: 999px;
    background: $ui-border-soft;
    overflow: hidden;

    span {
        display: block;
        height: 100%;
        border-radius: 999px;
        background: $ui-accent;
    }
}

.pmx-bar__value {
    text-align: right;
    font-weight: 600;
}

.pmx-bar__count {
    font-size: 12px;
    color: $ui-muted;
}

.pmx-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 6px 8px;
        text-align: left;
        font-weight: 500;
        color: $ui-muted;
        border-bottom: 1px solid $ui-border;
    }

    td {
        padding: 7px 8px;
        border-bottom: 1px solid $ui-border-soft;
        vertical-align: top;
    }

    .r {
        text-align: right;
    }
}

.pmx-click {
    cursor: pointer;

    &:hover td {
        background: $ui-bg-muted;
    }
}
</style>
